"use client";

import GrowthAnalyticsBridge from "@/components/GrowthAnalyticsBridge";

import Script from "next/script";
import type { RuntimePublicConfig } from "@/lib/config/publicRuntimeConfig";

function CloudPrefetchPatchScript() {
  return (
    <Script
      id="fix-prefetch-urls"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{
        __html: `
            (function() {
              // Only run in production (ikai.com.tr)
              if (window.location.hostname !== 'ikai.com.tr' &&
                  window.location.hostname !== 'www.ikai.com.tr') {
                return;
              }

              // Store original functions
              const originalFetch = window.fetch;
              const originalXHROpen = XMLHttpRequest.prototype.open;

              // Helper: Check if URL is a prefetch request
              function isPrefetchRequest(url) {
                if (typeof url !== 'string') return false;

                // Exclude Google Analytics and other third-party services
                if (url.includes('google-analytics.com') ||
                    url.includes('googletagmanager.com') ||
                    url.includes('doubleclick.net') ||
                    url.includes('analytics.google.com')) {
                  return false;
                }

                // Prefetch URLs typically:
                // 1. Use localhost:9205 or localhost:8205
                // 2. Are GET requests
                // 3. Don't contain /api/v1/ (API calls)
                // 4. Are page routes (not static assets)
                return (url.includes('localhost:9205') || url.includes('localhost:8205')) &&
                       !url.includes('/api/v1/') &&
                       !url.includes('/_next/static/') &&
                       !url.includes('/_next/webpack-hmr');
              }

              // Intercept fetch (for Next.js router prefetch)
              window.fetch = function(...args) {
                const url = args[0];

                if (isPrefetchRequest(url)) {
                  const fixedUrl = url.replace(
                    /https?:\\/\\/localhost:(9205|8205)/g,
                    window.location.origin
                  );
                  args[0] = fixedUrl;

                }

                return originalFetch.apply(this, args);
              };

              // Intercept XMLHttpRequest (backup for older prefetch methods)
              XMLHttpRequest.prototype.open = function(method, url, ...rest) {
                if (isPrefetchRequest(url) && method.toUpperCase() === 'GET') {
                  const fixedUrl = url.replace(
                    /https?:\\/\\/localhost:(9205|8205)/g,
                    window.location.origin
                  );

                  return originalXHROpen.call(this, method, fixedUrl, ...rest);
                }

                return originalXHROpen.call(this, method, url, ...rest);
              };

            })();
          `,
      }}
    />
  );
}

/**
 * Client-side Scripts Component
 * Prevents hydration warnings from nonce attributes
 */
export default function ClientScripts({
  runtimeConfig,
}: {
  runtimeConfig: RuntimePublicConfig;
}) {
  const GA_ID = runtimeConfig.integrations.gaMeasurementId;
  const analyticsEnabled =
    process.env.NODE_ENV === "production" &&
    runtimeConfig.features.analytics === true &&
    Boolean(GA_ID);
  const cloudPrefetchPatchEnabled = runtimeConfig.profile === "cloud";

  return (
    <>
      <GrowthAnalyticsBridge analyticsEnabled={analyticsEnabled} />
      {/* Remove invalid preload link - Fix: <link rel=preload> uses an unsupported 'as' value */}
      <Script
        id="remove-invalid-preload"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            (function() {
              function removeInvalidPreload() {
                const invalidPreload = document.querySelector('link[rel="preload"][href="/"][as="document"]');
                if (invalidPreload) {
                  invalidPreload.remove();
                }
              }
              if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', removeInvalidPreload);
              } else {
                removeInvalidPreload();
              }
            })();
          `,
        }}
      />
      {cloudPrefetchPatchEnabled ? <CloudPrefetchPatchScript /> : null}
      {analyticsEnabled ? (
        <>
          {/* Google Analytics 4 - Lazy loaded to reduce render-blocking */}
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`}
            strategy="lazyOnload"
          />
          <Script id="google-analytics" strategy="lazyOnload">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${GA_ID}');
            `}
          </Script>
        </>
      ) : null}
    </>
  );
}
